import {
  WatchlistBaseModel,
  WatchlistEntityRequest,
  WatchlistGeneralRequest,
  WatchlistIndividualRequest,
  WatchlistListsModel,
} from '../domain/watchlist/WatchlistDomains';
import CountriesData from './CountriesData';
import { WatchlistType } from '../constant/Constants';
import { MatchManyLog, MatchManyLogRequest, WatchlistHistoryView } from '../domain/watchlist/WatchlistHistoryDomains';

class WatchlistUtil {
  public static initializeIndividualKycRequest = (): WatchlistIndividualRequest => {
    return {
      id: null,
      threshold: 50,
      name: null,
      email: null,
      phone: null,
      dateOfBirth: null,
      citizenship: null,
      identification: null,
      lists: [],
      referenceId: null,
    };
  };

  public static initializeGeneralKycRequest = (): WatchlistGeneralRequest => {
    return {
      id: null,
      threshold: 50,
      name: null,
      email: null,
      phone: null,
      date: null,
      lists: [],
      referenceId: null,
    };
  };

  public static initializeEntityKycRequest = (): WatchlistEntityRequest => {
    return {
      id: null,
      threshold: 50,
      email: null,
      phone: null,
      name: null,
      dateOfRegistration: null,
      identifier: null,
      lists: [],
      referenceId: null,
    };
  };

  public static buildRequestFromSearchParams = (
    searchParams: URLSearchParams,
  ): WatchlistIndividualRequest | WatchlistEntityRequest | WatchlistGeneralRequest | undefined => {
    const type: string | null = searchParams.get('type');
    const name: string | null = searchParams.get('name');
    const email: string | null = searchParams.get('email');
    const phone: string | null = searchParams.get('phone');
    const threshold: string | null = searchParams.get('threshold');
    const lists: string | null = searchParams.get('lists');
    const referenceId: string | null = searchParams.get('referenceId');
    const parsedLists = lists && lists.length > 0 ? lists.split(',') : [];
    const parsedThreshold = Number(threshold);

    switch (type as WatchlistType) {
      case 'Individual':
        return {
          id: null,
          threshold: parsedThreshold,
          name: name,
          dateOfBirth: searchParams.get('dateOfBirth'),
          citizenship: searchParams.get('citizenship'),
          identification: searchParams.get('identification'),
          email: email,
          phone: phone,
          referenceId: referenceId,
          lists: parsedLists,
        };
      case 'Entity':
        return {
          id: null,
          threshold: parsedThreshold,
          name: name,
          email: email,
          phone: phone,
          dateOfRegistration: searchParams.get('dateOfRegistration'),
          identifier: searchParams.get('identifier'),
          referenceId: referenceId,
          lists: parsedLists,
        };
      case 'General':
        return {
          threshold: parsedThreshold,
          name: name,
          email: email,
          phone: phone,
          date: searchParams.get('date'),
          id: searchParams.get('id'),
          referenceId: referenceId,
          lists: parsedLists,
        };
      default:
        return undefined;
    }
  };

  public static getTypeFromMatchType = (matchType: string): WatchlistType | undefined => {
    switch (matchType.toUpperCase()) {
      case 'INDIVIDUAL':
        return 'Individual';
      case 'ENTITY':
        return 'Entity';
      case 'GENERAL':
        return 'General';
      default:
        return undefined;
    }
  };

  public static buildHistoryView = (log: MatchManyLog): WatchlistHistoryView | undefined => {
    const type = WatchlistUtil.getTypeFromMatchType(log.matchType);
    if (type == undefined) return undefined;
    const logRequest: MatchManyLogRequest = log.request ?? {};
    const threshold = logRequest.threshold ?? log.threshold;
    const name = logRequest.name ?? log.searchedName ?? null;
    const email = logRequest.email ?? null;
    const phone = logRequest.phone ?? null;
    const lists = logRequest.lists ?? [];
    const referenceId = logRequest.referenceId ?? log.referenceId ?? null;
    const matches = log.matches ?? [];
    const createdAt = Number(log.createdAt);

    switch (type) {
      case 'Individual': {
        const request: WatchlistIndividualRequest = {
          id: null,
          threshold,
          name,
          email,
          phone,
          identification: logRequest.identification ?? null,
          dateOfBirth: logRequest.dateOfBirth ?? null,
          citizenship: logRequest.citizenship ?? null,
          lists,
          referenceId,
        };
        return { logId: log.id, type, request, matches, createdAt };
      }
      case 'Entity': {
        const request: WatchlistEntityRequest = {
          id: null,
          threshold,
          name,
          email,
          phone,
          identifier: logRequest.identifier ?? null,
          dateOfRegistration: logRequest.dateOfRegistration ?? null,
          lists,
          referenceId,
        };
        return { logId: log.id, type, request, matches, createdAt };
      }
      case 'General': {
        const request: WatchlistGeneralRequest = {
          id: logRequest.id ?? null,
          threshold,
          name,
          email,
          phone,
          date: logRequest.date ?? null,
          lists,
          referenceId,
        };
        return { logId: log.id, type, request, matches, createdAt };
      }
    }
  };

  public static getMonth = (date: number) => {
    switch (date) {
      case 1:
        return 'January';
      case 2:
        return 'February';
      case 3:
        return 'March';
      case 4:
        return 'April';
      case 5:
        return 'May';
      case 6:
        return 'June';
      case 7:
        return 'July';
      case 8:
        return 'August';
      case 9:
        return 'September';
      case 10:
        return 'October';
      case 11:
        return 'November';
      case 12:
        return 'December';
      default:
        return '';
    }
  };

  public static phoneList = () => {
    return [
      { label: 'TR (+90)', value: '+90' },
      { label: 'US (+1)', value: '+1' },
      { label: 'CN (+86)', value: '+86' },
      { label: 'IN (+91)', value: '+91' },
      { label: 'JP (+81)', value: '+81' },
      { label: 'DE (+49)', value: '+49' },
      { label: 'UK (+44)', value: '+44' },
      { label: 'FR (+33)', value: '+33' },
      { label: 'IT (+39)', value: '+39' },
      { label: 'RU (+7)', value: '+7' },
      { label: 'BR (+55)', value: '+55' },
      { label: 'CA (+1)', value: '+1' },
      { label: 'KR (+82)', value: '+82' },
      { label: 'AU (+61)', value: '+61' },
      { label: 'ES (+34)', value: '+34' },
      { label: 'MX (+52)', value: '+52' },
      { label: 'ID (+62)', value: '+62' },
      { label: 'SA (+966)', value: '+966' },
      { label: 'ZA (+27)', value: '+27' },
      { label: 'AR (+54)', value: '+54' },
    ];
  };

  public static getCitizenshipList = () => {
    return CountriesData.countries.map(({ code, name }) => ({
      label: `${name} - ${code}`,
      value: name,
    }));
  };

  public static isCustomList(list: WatchlistListsModel | undefined): boolean {
    if (list == undefined || list.name == undefined) {
      return false;
    }
    return list.name.trim().toLowerCase() === 'custom';
  }

  public static getCustomListIdFromPath(pathname: string): string | undefined {
    const segments: string[] = pathname.split('/').filter((segment: string) => segment.length > 0);
    const index: number = segments.indexOf('custom-list');
    if (index < 0 || index + 1 >= segments.length) {
      return undefined;
    }
    return segments[index + 1];
  }

  public static getPathSearchParams(pathname: string): URLSearchParams {
    const params: URLSearchParams = new URLSearchParams();
    const segments: string[] = pathname.split('/').filter((segment: string) => segment.length > 0);
    if (segments.length > 0) {
      const lastSegment: string = segments[segments.length - 1];
      let pathParams: URLSearchParams;
      try {
        pathParams = new URLSearchParams(decodeURIComponent(lastSegment));
      } catch (e) {
        pathParams = new URLSearchParams(lastSegment);
      }
      pathParams.forEach((value: string, key: string) => {
        params.set(key, value);
      });
    }
    // watchlist/detail URL often splits on '&' into location.search
    const queryParams: URLSearchParams = new URLSearchParams(window.location.search);
    queryParams.forEach((value: string, key: string) => {
      if (!params.has(key) || (params.get(key) ?? '').length === 0) {
        params.set(key, value);
      }
    });
    return params;
  }

  public static getLatestResultTimestamp = (results: WatchlistBaseModel[] | undefined): number | undefined => {
    if (results == undefined || results.length == 0) {
      return undefined;
    }
    return results[results.length - 1].timestamp;
  };
}

export default WatchlistUtil;
